import { Button, Table, Tag, Input, Select, message, Tooltip } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { opportunityApi } from '@/api/opportunityApi';
import { useAuth } from '@/auth/useAuth';
import { daysUntil, getUrgency as computeUrgency, parseDateLocal } from '@/utils/dateFormatters';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import type { OpportunityListItem, OpportunityStatus } from '@/types/opportunity';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

// Quick urgency filter chips shown in the sticky toolbar.
const URGENCY_CHIPS: { value: string | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'APPROACHING', label: 'Approaching' },
  { value: 'ON_TRACK', label: 'On Track' },
  { value: 'NONE', label: 'No Date' },
];

const statusColors: Record<OpportunityStatus, string> = {
  NEW: 'blue',
  INTAKE_COMPLETE: 'cyan',
  QUALIFIED: 'geekblue',
  BID_GO: 'green',
  BID_NO_GO: 'red',
  PLANNING: 'purple',
  CONTENT_DEV: 'orange',
  IN_REVIEW: 'gold',
  CEO_REVIEW: 'volcano',
  SUBMITTED: 'lime',
  POST_SUBMISSION: 'magenta',
  WON: 'success',
  LOST: 'error',
  CANCELLED: 'default',
};

const statusLabels: Record<OpportunityStatus, string> = {
  NEW: 'New',
  INTAKE_COMPLETE: 'Intake Complete',
  QUALIFIED: 'Qualified',
  BID_GO: 'Bid - Go',
  BID_NO_GO: 'Bid - No Go',
  PLANNING: 'Planning',
  CONTENT_DEV: 'Content Dev',
  IN_REVIEW: 'In Review',
  CEO_REVIEW: 'CEO Review',
  SUBMITTED: 'Submitted',
  POST_SUBMISSION: 'Post Submission',
  WON: 'Won',
  LOST: 'Lost',
  CANCELLED: 'Cancelled',
};

const OpportunityListPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<OpportunityListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    searchParams.get('status') || undefined,
  );
  const [phaseFilter, setPhaseFilter] = useState<string | undefined>(undefined);
  const [urgencyFilter, setUrgencyFilter] = useState<string | undefined>(
    searchParams.get('urgency') || undefined,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showTotal: (total) => `Total ${total} opportunities`,
  });

  const fetchData = useCallback(async (page = 0, size = 20, searchTerm?: string) => {
    setLoading(true);
    try {
      const response = await opportunityApi.getAll({
        page,
        size,
        search: searchTerm || undefined,
        excludeStatuses: ['SUBMITTED', 'POST_SUBMISSION', 'WON', 'LOST'],
      });
      const HIDDEN_STATUSES = ['SUBMITTED', 'POST_SUBMISSION', 'WON', 'LOST'];
      setData(response.content.filter((r) => !HIDDEN_STATUSES.includes(r.status)));
      setPagination((prev) => ({
        ...prev,
        current: response.pageable.pageNumber + 1,
        pageSize: response.pageable.pageSize,
        total: response.totalElements,
      }));
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
      message.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlUrgency = searchParams.get('urgency') || undefined;
    const urlStatus = searchParams.get('status') || undefined;
    setSearch(urlSearch);
    setUrgencyFilter(urlUrgency);
    setStatusFilter(urlStatus);
    fetchData(0, 20, urlSearch || undefined);
  }, [fetchData, searchParams]);

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    fetchData(
      (newPagination.current || 1) - 1,
      newPagination.pageSize || 20,
      search || undefined,
    );
  };

  const handleSearch = () => {
    fetchData(0, pagination.pageSize || 20, search || undefined);
  };

  // Browser's local timezone — was hardcoded to America/Los_Angeles, which
  // mis-rendered datetimes for any user outside Pacific. Using the user's
  // resolved timezone makes lastActivityAt / Created tooltips show wall-clock time.
  const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toUtcMs = (value: string) => {
    // Only append 'Z' for datetime strings (contain 'T'); date-only strings are already UTC
    if (value.includes('T') && !value.endsWith('Z') && !value.includes('+')) {
      return new Date(value + 'Z').getTime();
    }
    return new Date(value).getTime();
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    // Date-only strings ("YYYY-MM-DD") must be parsed in local time, not UTC,
    // otherwise west-of-UTC users see the previous day. Datetime strings keep
    // their original Pacific-TZ display.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return parseDateLocal(value).format('MMM D, YYYY');
    }
    return new Date(toUtcMs(value)).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: TZ,
    });
  };

  const formatRelativeTime = (value?: string) => {
    if (!value) return '-';
    const diff = Date.now() - toUtcMs(value);
    if (diff < 0) return 'just now';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(value);
  };

  const phaseColors: Record<string, string> = {
    PHASE_A: 'purple', PHASE_B: 'blue', PHASE_C: 'cyan',
    PHASE_D: 'green', PHASE_E: 'gold', PHASE_F: 'magenta',
  };

  const phaseLabels: Record<string, string> = {
    PHASE_A: 'Gate A', PHASE_B: 'Gate B', PHASE_C: 'Gate C',
    PHASE_D: 'Gate D', PHASE_E: 'Gate E', PHASE_F: 'Gate F',
  };

  const columns: ColumnsType<OpportunityListItem> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (text: string, record: OpportunityListItem) => (
        <a
          className="ui-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/opportunities/${record.id}`);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Client / Agency',
      dataIndex: 'agencyName',
      key: 'agencyName',
      sorter: (a, b) => (a.agencyName || '').localeCompare(b.agencyName || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: OpportunityStatus) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      sorter: (a, b) => {
        const aT = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bT = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aT - bT;
      },
      render: formatDate,
    },
    {
      title: 'Days Left',
      dataIndex: 'dueDate',
      key: 'daysLeft',
      sorter: (a, b) => {
        const aT = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bT = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aT - bT;
      },
      render: (dueDate?: string) => {
        if (!dueDate) return '-';
        const days = daysUntil(dueDate);
        if (days < 0) return <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>Overdue</span>;
        if (days === 0) return <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>Today</span>;
        return `${days}d`;
      },
      align: 'center' as const,
    },
    {
      title: 'Urgency',
      dataIndex: 'dueDate',
      key: 'urgency',
      render: (dueDate?: string) => {
        const u = computeUrgency(dueDate);
        if (u === 'NONE') return <Tag color="default">No Date</Tag>;
        if (u === 'OVERDUE') return <Tag color="error">Overdue</Tag>;
        if (u === 'CRITICAL') return <Tag color="error">Critical</Tag>;
        if (u === 'APPROACHING') return <Tag color="warning">Approaching</Tag>;
        return <Tag color="success">On Track</Tag>;
      },
    },
    {
      title: 'Phase',
      dataIndex: 'currentPhase',
      key: 'currentPhase',
      render: (phase: string) =>
        phase ? (
          <Tag color={phaseColors[phase] || 'default'} style={{ fontSize: 11 }}>
            {phaseLabels[phase] || phase}
          </Tag>
        ) : (
          <Tag color="default" style={{ fontSize: 11 }}>—</Tag>
        ),
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityAt',
      key: 'lastActivityAt',
      render: (value: string) => (
        <Tooltip title={value ? new Date(toUtcMs(value)).toLocaleString('en-US', { timeZone: TZ }) : undefined}>
          <span style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>{formatRelativeTime(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Discovered By',
      dataIndex: 'discoveredBy',
      key: 'discoveredBy',
      render: (text: string) => text || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => {
        const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aT - bT;
      },
      render: formatDate,
    },
  ];

  // Apply client-side filters
  const filteredData = data.filter((row) => {
    if (statusFilter && row.status !== statusFilter) return false;
    if (phaseFilter && row.currentPhase !== phaseFilter) return false;
    if (urgencyFilter && computeUrgency(row.dueDate) !== urgencyFilter) return false;
    return true;
  });

  const resetAll = () => {
    setSearch('');
    setStatusFilter(undefined);
    setPhaseFilter(undefined);
    setUrgencyFilter(undefined);
    setSelectedRowKeys([]);
    setSearchParams({});
  };

  const hasActiveFilters = !!(search || statusFilter || phaseFilter || urgencyFilter);

  // Client-side CSV export — exports selection if any rows are selected,
  // otherwise the current filtered view. No backend dependency.
  const exportCsv = () => {
    const rows = selectedRowKeys.length
      ? filteredData.filter((r) => selectedRowKeys.includes(r.id))
      : filteredData;
    if (!rows.length) {
      message.info('Nothing to export');
      return;
    }
    const headers = ['Title', 'Client/Agency', 'Status', 'Phase', 'Due Date', 'Days Left', 'Discovered By', 'Created'];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map((r) => {
      const days = r.dueDate ? daysUntil(r.dueDate) : null;
      return [
        r.title,
        r.agencyName,
        statusLabels[r.status] || r.status,
        r.currentPhase ? (phaseLabels[r.currentPhase] || r.currentPhase) : '',
        formatDate(r.dueDate),
        days === null ? '' : days < 0 ? 'Overdue' : `${days}d`,
        r.discoveredBy,
        formatDate(r.createdAt),
      ].map(esc).join(',');
    });
    const csv = [headers.map(esc).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunities-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(`Exported ${rows.length} ${rows.length === 1 ? 'opportunity' : 'opportunities'}`);
  };

  return (
    <div>
      <PageHeader
        title="Opportunities"
        subtitle={`${pagination.total ?? 0} total · ${filteredData.length} shown`}
        actions={
          <>
            <Button icon={<DownloadOutlined />} onClick={exportCsv}>
              Export
            </Button>
            {userRole !== 'CONTRIBUTOR' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/opportunities/new')}>
                New Opportunity
              </Button>
            )}
          </>
        }
      />

      {/* Sticky toolbar: search + quick urgency chips + dropdown filters */}
      <div className="ui-toolbar">
        <Input
          placeholder="Search opportunities…"
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={handleSearch}
          onClear={handleSearch}
          style={{ maxWidth: 260 }}
          allowClear
        />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {URGENCY_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              className="ui-chip ui-focusable"
              data-active={urgencyFilter === chip.value || (!urgencyFilter && chip.value === undefined)}
              onClick={() => setUrgencyFilter(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <Select
          placeholder="Status"
          allowClear
          value={statusFilter}
          onChange={setStatusFilter}
          className="filter-select"
          style={{ width: 150 }}
          options={Object.entries(statusLabels)
            .filter(([k]) => !['SUBMITTED', 'POST_SUBMISSION', 'WON', 'LOST'].includes(k))
            .map(([value, label]) => ({ value, label }))}
        />
        <Select
          placeholder="Phase"
          allowClear
          value={phaseFilter}
          onChange={setPhaseFilter}
          className="filter-select"
          style={{ width: 130 }}
          options={Object.entries(phaseLabels).map(([value, label]) => ({ value, label }))}
        />
        {hasActiveFilters && (
          <Button icon={<ReloadOutlined />} onClick={resetAll}>
            Reset
          </Button>
        )}
      </div>

      {/* Bulk action bar — appears when rows are selected */}
      {selectedRowKeys.length > 0 && (
        <div
          className="ui-animate-in"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            marginBottom: 12,
            borderRadius: 12,
            background: 'var(--color-primary-light)',
            border: '1px solid var(--color-primary)',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
            {selectedRowKeys.length} selected
          </span>
          <Button size="small" icon={<DownloadOutlined />} onClick={exportCsv}>
            Export selected
          </Button>
          {selectedRowKeys.length === 1 && (
            <Button
              size="small"
              icon={<FolderOpenOutlined />}
              onClick={() => navigate(`/opportunities/${selectedRowKeys[0]}`)}
            >
              Open
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setSelectedRowKeys([])}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          size="middle"
          scroll={{ x: 1500 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            columnWidth: 44,
          }}
          locale={{
            emptyText: loading ? (
              <span />
            ) : (
              <EmptyState
                icon={<FolderOpenOutlined />}
                title={
                  userRole === 'CONTRIBUTOR'
                    ? 'No opportunities assigned yet'
                    : hasActiveFilters
                      ? 'No matches'
                      : 'No opportunities yet'
                }
                description={
                  userRole === 'CONTRIBUTOR'
                    ? "You'll see opportunities here once an admin assigns you to a task."
                    : hasActiveFilters
                      ? 'Try clearing filters or adjusting your search.'
                      : 'Create your first opportunity to get started.'
                }
                action={
                  hasActiveFilters ? (
                    <Button icon={<ReloadOutlined />} onClick={resetAll}>Clear filters</Button>
                  ) : userRole !== 'CONTRIBUTOR' ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/opportunities/new')}>
                      New Opportunity
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/opportunities/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </div>
  );
};

export default OpportunityListPage;
